import { Injectable } from '@angular/core';
import { TokenService } from '@cargos/sprintpay-services';
import { Observable, map } from 'rxjs';
import { CanCreateApprovalFlow, PaymentReferenceLookups } from '../models/approval-flow-levels';
import { ConfigService } from './config.service';
import { CustomerService } from './customer.service';
import { JwtHelperService } from './jwt-helper.service';
import { SessionService } from './session.service';

@Injectable({
    providedIn: 'root',
})
export class ApprovalFlowLevelsService {
    constructor(
        private sessionService: SessionService,
        private tokenService: TokenService,
        private configService: ConfigService,
        private customerService: CustomerService,
        private jwtHelperService: JwtHelperService
    ) {}

    isEnableRequestorApprovalFlow(): Observable<boolean> {
        return this.sessionService.isAuthenticated$().pipe(
            map((isAuthenticated) => {
                if (!isAuthenticated) return false;

                return (
                    !this.jwtHelperService.isTokenExpired(this.tokenService.instant_token) &&
                    this.customerService.isRequestorUser() &&
                    this.configService.isEnableRequestorApprovalFlow()
                );
            })
        );
    }

    isChargeToPayAvailable(chargeAmount: number): boolean {
        const levelsByFlow = this.getLevelsOfApprovalFlowByAmount(chargeAmount);
        if (levelsByFlow === 1) {
            return true;
        }
        return false;
    }

    getPaymentAmounts(): PaymentReferenceLookups[] {
        const customer = this.customerService.getCustomer();

        return (
            customer.approvalLevels?.company?.paymentReference?.find(
                (reference) => reference.name?.toLowerCase() === 'type'
            )?.paymentReferenceLookups ?? []
        );
    }

    getLevelsOfApprovalFlowByAmount(amount: number): number {
        const type = this.getTypeByAmount(amount);
        if (type) {
            return this.getApprovalFlow(type)?.approvalFlowLevels?.length || 0;
        }
        return 0;
    }

    getApprovalFlow(type: string): CanCreateApprovalFlow | undefined {
        const customer = this.customerService.getCustomer();
        return (customer.approvalLevels?.canCreateApprovalFlow || []).find((approvalLevel: CanCreateApprovalFlow) =>
            approvalLevel.name?.includes(type)
        );
    }

    getTypeByAmount(amount: number): string {
        const paymentType = this.getPaymentAmounts();
        let type: string = '';

        paymentType?.forEach((paymentAmount: PaymentReferenceLookups) => {
            const criteriaToValidate = paymentAmount.criteria?.split(',');

            const min = Number(criteriaToValidate?.[0] ?? '');
            const max = Number(criteriaToValidate?.[1] ?? '');

            if (amount >= min && amount <= max) {
                type = paymentAmount.type || '';
                return;
            }
        });
        return type;
    }
}
